import { Box } from '@mui/material';
import React, { useContext, useEffect, useRef, useState } from 'react';
import PowerCard from './PowerCard';
import MapCard from './MapCard';
import { useDashboardContext } from '../../../components/providers/DashboardProvider';
import { useAuth } from '../../../components/providers/AuthProvider';
import AnimationCard from './AnimationCard';
import BatteryCard from './BatteryCard';
import SavingCard from './SavingCard';
import OverviewCard from './OverviewCard';
import { splitGridEnergy } from '../grid/energy';
import RawDataPanel from '../RawDataPanel';

const OverviewPage = () => {

  const { user } = useAuth();
  const isAdmin = user?.profile?.user_type === 'admin';

  const {
    selectedCluster
  } = useDashboardContext();

  // Animation box
  const [solarPower, setSolarPower] = useState(null);
  const [loadPower, setLoadPower] = useState(null);
  const [pcsPower, setPcsPower] = useState(null);

  // Savings box
  // Utility bill
  const [saving, setSaving] = useState(null);
  const [savingPct, setSavingPct] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(5); // May
  const [selectedYear, setSelectedYear] = useState(2026);

  // Battery box
  const [soc, setSoc] = useState(null);
  const [batteryStatus, setBatteryStatus] = useState(null);
  const [batteryPower, setBatteryPower] = useState(null);
  const [socSeries, setSocSeries] = useState([]);

  // Power box
  const [gridPower, setGridPower] = useState(null);
  const [gridSeries, setGridSeries] = useState([]);
  const [loadSeries, setLoadSeries] = useState([]);
  const [solarSeries, setSolarSeries] = useState([]);

  // PCS
  const [pcsChargeSeries, setPcsChargeSeries] = useState([]);
  const [pcsDischargeSeries, setPcsDischargeSeries] = useState([]);


  const {
    pollingCounter,
    fetchClusterOverview,
    overviewData,
    fetchClusterUtilityBillData,
    fetchClusterUtilityBillPeriods, 
    utilityBillData, 
    utilityBillPeriods, 
  } = useDashboardContext();

  const processBatteryData = (batteryData) => {
    if (batteryData.cluster_state) {
      setBatteryStatus(batteryData.cluster_state);
    }
    if (batteryData.cluster_SOC_avg !== null) {
      setSoc(batteryData.cluster_SOC_avg);
    }
    if (batteryData.cluster_P_total !== null) {
      setBatteryPower(batteryData.cluster_P_total);
    }

    // Timeseries data
    let stringIndex = 0;
    let ts_soc = []
    if (batteryData.blocks_data) {
      // Go through all blocks
      Object.keys(batteryData.blocks_data).forEach(blockId => {
        const blockData = batteryData.blocks_data[blockId];
        if (blockData.battery_strings_count && blockData.battery_strings_data) {

          // Go through all strings in block
          Object.keys(blockData.battery_strings_data).forEach(stringId => {
            // console.log("String ID", stringId);
            const stringData = blockData.battery_strings_data[stringId];
            // console.log(stringData);
            if (stringData.string_data_ts_hour) {
              ts_soc.push({
                x: [],
                y: [],
                name: stringId
              });
              // Go through all data points
              Object.keys(stringData.string_data_ts_hour).forEach(epoch => {
                const dataPoint = stringData.string_data_ts_hour[epoch];
                // console.log(dataPoint);
                // SOC
                ts_soc[stringIndex]["x"].push(dataPoint.local_datetime_str);
                ts_soc[stringIndex]["y"].push(dataPoint.SOC);
              });
              stringIndex += 1;
            }
          })
        }
      })
    }
    // console.log(ts);
    setSocSeries(ts_soc);
  }

  const processGridData = (gridData) => {
    setGridPower(null);
    if (gridData.cluster_P_total) {
      setGridPower(gridData.cluster_P_total);
    }

    // Timeseries data
    let meterIndex = 0;
    let ts = [];
    // Go through all blocks
    if (gridData.blocks_data) {
      Object.keys(gridData.blocks_data).forEach(blockId => {
        const blockData = gridData.blocks_data[blockId];
        if (blockData.meters_count && blockData.meters_data) {

          // Go through all meters in block
          Object.keys(blockData.meters_data).forEach(meterId => {
            // console.log("Meter ID", meterId);
            const meterData = blockData.meters_data[meterId];
            // console.log(meterData);
            if (meterData.meter_data_ts_hour) {
              ts.push({
                x: [],
                yImport: [],
                yExport: [],
                name: meterId
              });

              // Go through all data points
              Object.keys(meterData.meter_data_ts_hour).forEach(epoch => {
                const dataPoint = meterData.meter_data_ts_hour[epoch];
                const { importE, exportE } = splitGridEnergy(dataPoint);
                ts[meterIndex]["x"].push(dataPoint.local_datetime_str);
                ts[meterIndex]["yImport"].push(importE);
                ts[meterIndex]["yExport"].push(exportE);
              });
              meterIndex += 1;
            }

          });
        }
      });

    }
    setGridSeries(ts);
  }

  const processSolarData = (solarData) => {
    // console.log(solarData);
    setSolarPower(null);
    if (solarData.cluster_P_total) {
      setSolarPower(solarData.cluster_P_total);
    }

    // Timeseries data, as production (the solar meter reads production as negative)
    let ts = [];
    if (solarData.blocks_data) {
      Object.keys(solarData.blocks_data).forEach(blockId => {
        const blockData = solarData.blocks_data[blockId];
        if (blockData.meters_data) {
          Object.keys(blockData.meters_data).forEach(meterId => {
            const meterData = blockData.meters_data[meterId];
            if (meterData.meter_data_ts_hour) {
              const dataset = { x: [], y: [], name: meterId };
              Object.keys(meterData.meter_data_ts_hour).forEach(epoch => {
                const dataPoint = meterData.meter_data_ts_hour[epoch];
                dataset.x.push(dataPoint.local_datetime_str);
                dataset.y.push(dataPoint.E_active != null ? -dataPoint.E_active : null);
              });
              ts.push(dataset);
            }
          });
        }
      });
    }
    setSolarSeries(ts);
  }

  const processPcsData = (pcsData) => {
    setPcsPower(pcsData.cluster_P_total ?? null);

    // Timeseries data
    let pcsIndex = 0;
    let ts_charge = [];
    let ts_discharge = [];
    let ts_net = {
      x: [],
      y: [],
      name: "PCS_net",
    }

    // Go through all blocks
    if (pcsData.blocks_data) {
      Object.keys(pcsData.blocks_data).forEach(blockId => {
        const blockData = pcsData.blocks_data[blockId];
        if (blockData.pcs_count && blockData.pcs_data) {

          // Go through all PCSs in block
          Object.keys(blockData.pcs_data).forEach(pcsId => {
            // console.log("PCS ID", pcsId);

            const pcsData = blockData.pcs_data[pcsId];
            // console.log(pcsData);
            if (pcsData.pcs_data_ts_hour) {
              ts_charge.push({
                x: [],
                y: [],
                name: pcsId
              });
              ts_discharge.push({
                x: [],
                y: [],
                name: pcsId
              });

              // Go through all data points
              Object.keys(pcsData.pcs_data_ts_hour).forEach(epoch => {
                const dataPoint = pcsData.pcs_data_ts_hour[epoch];

                ts_charge[pcsIndex]["x"].push(dataPoint.local_datetime_str);
                ts_charge[pcsIndex]["y"].push(dataPoint.E_AC_charge);

                ts_discharge[pcsIndex]["x"].push(dataPoint.local_datetime_str);
                ts_discharge[pcsIndex]["y"].push(dataPoint.E_AC_discharge);
              });
              pcsIndex += 1;
            }
          });
        }
      });
    }
    setPcsChargeSeries(ts_charge);
    setPcsDischargeSeries(ts_discharge);
  }

  const processLoadData = (loadData) => {
    setLoadPower(null);
    if (loadData.cluster_P_total) {
      setLoadPower(loadData.cluster_P_total);
    }
  }

  const runRoutine = async () => {
    // console.log("Polling in overview");
    fetchClusterOverview();
  }

  useEffect(() => {
    if (pollingCounter != 0) {
      runRoutine();
    }
  }, [pollingCounter]);

  useEffect(() => {
    if (overviewData) {

      // Battery data
      if (overviewData.battery) {
        processBatteryData(overviewData.battery);
      }

      // Grid data
      if (overviewData.grid) {
        processGridData(overviewData.grid);
      }
      
      // Solar data
      if (overviewData.solar) {
        processSolarData(overviewData.solar);
      } else {
        setSolarSeries([]);
      }

      // PCS data
      if (overviewData.pcs) {
        processPcsData(overviewData.pcs);
      }

      // Load data
      if (overviewData.load) {
        processLoadData(overviewData.load);
      }

    }
  }, [overviewData]);


  useEffect(() => {
    if (utilityBillData && utilityBillData.comparison) {
      try {
        const parsed = JSON.parse(utilityBillData.comparison);
        // Process utility bill data here
        console.log(parsed);
        setSaving(-parsed.summary?.total_diff);
        setSavingPct(-parsed.summary?.pct_change);
      } catch (e) {
        console.error('Could not parse the utility bill data.', e);
      }
      // Process utility bill data here
      // console.log(utilityBillData);
    }
  }, [utilityBillData]);

  useEffect(() => {
    fetchClusterUtilityBillPeriods().then((periods) => {
      if (!periods || periods.length === 0) return;
      const latest = periods.reduce((best, p) =>
        p.year > best.year || (p.year === best.year && p.month > best.month) ? p : best
      );
      setSelectedYear(latest.year);
      setSelectedMonth(latest.month);
      fetchClusterUtilityBillData(latest.year, latest.month);
    });
  }, []);

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr", lg: "repeat(6, 1fr)" },
        gap: 2,
        pt: 2,
      }}
    >
      {/* Row 1: Energy flow and Savings */}
      <OverviewCard title="Energy flow" sx={{ gridColumn: { lg: "span 4" } }}>
        <AnimationCard
          gridPower={gridPower}
          batterySOC={soc}
          batteryStatus={batteryStatus}
          batteryPower={batteryPower}
          pcsPower={pcsPower}
          solarPower={solarPower}
          loadPower={loadPower}
        />
      </OverviewCard>

      <OverviewCard title="Monthly savings" sx={{ gridColumn: { lg: "span 2" } }} contentSx={{ p: 0 }}>
        <SavingCard
          year={selectedYear}
          month={selectedMonth}
          savings={saving}
          pct={savingPct}
        />
      </OverviewCard>

      {/* Row 2: Battery and Power */}
      <OverviewCard title="Battery" sx={{ gridColumn: { lg: "span 3" } }}>
        <BatteryCard
          soc={soc}
          statusText={batteryStatus}
          series={socSeries}
        />
      </OverviewCard>

      <OverviewCard title="Power flow" sx={{ gridColumn: { lg: "span 3" } }}>
        <PowerCard
          power={gridPower}
          seriesGrid={gridSeries}
          seriesSolar={solarSeries}
          seriesCharge={pcsChargeSeries}
          seriesDischarge={pcsDischargeSeries}
        />
      </OverviewCard>

      {/* Row 3: Map */}
      <OverviewCard
        title="Site location"
        subtitle={selectedCluster?.address}
        sx={{ gridColumn: { lg: "span 6" } }}
        contentSx={{ p: 0, pt: 1.5 }}
      >
        <MapCard
          lat={selectedCluster?.lat}
          lon={selectedCluster?.lon}
        />
      </OverviewCard>

      {/* Both responses feed this page: overview for the live cards, utility bill for savings */}
      {(overviewData || utilityBillData) && isAdmin && (
        <Box sx={{ gridColumn: "1 / -1" }}>
          <RawDataPanel data={{ overview: overviewData, utility_bill: utilityBillData }} />
        </Box>
      )}
    </Box>
  )
}

export default OverviewPage